import React, { useEffect, useState } from 'react';
import { clientSectorAPI } from '../api';
import { toast } from 'sonner';
import { Plus, Pencil, Check, X } from 'lucide-react';

// Client Sector master. Rename + activate/deactivate only — sector_id (analytics identity) is permanent.
export const ClientSectorMaster = () => {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState('');

  const load = () => clientSectorAPI.listAll().then((r) => setRows(r.data || [])).catch(() => toast.error('Failed to load sectors'));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    try { await clientSectorAPI.create({ name: newName.trim() }); setNewName(''); toast.success('Sector added'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not add sector'); }
  };

  const rename = async (sectorId) => {
    try { await clientSectorAPI.update(sectorId, { name: editName.trim() }); setEditing(null); toast.success('Sector renamed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not rename sector'); }
  };

  const toggle = async (row) => {
    try { await clientSectorAPI.update(row.sector_id, { is_active: !row.is_active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update sector'); }
  };

  return (
    <div className="space-y-4" data-testid="settings-sectors-tab">
      <div>
        <h2 className="text-xl font-outfit font-medium">Client Sectors</h2>
        <p className="text-sm text-muted-foreground">Deactivate instead of deleting — historical lead analytics keep resolving the sector.</p>
      </div>

      <div className="flex gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New sector name"
          data-testid="sector-new-name" className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
        <button onClick={add} data-testid="sector-add-btn"
          className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground dark:bg-slate-800">
            <tr><th className="px-3 py-2">Sector</th><th className="px-3 py-2">ID</th><th className="px-3 py-2 text-center">Active</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.sector_id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`sector-row-${r.sector_id}`}>
                <td className="px-3 py-2">
                  {editing === r.sector_id ? (
                    <input value={editName} onChange={(e) => setEditName(e.target.value)} data-testid={`sector-edit-input-${r.sector_id}`}
                      className="w-full rounded border border-slate-200 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900" />
                  ) : r.name}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.sector_id}</td>
                <td className="px-3 py-2 text-center">
                  <input type="checkbox" checked={r.is_active} onChange={() => toggle(r)} data-testid={`sector-active-${r.sector_id}`} />
                </td>
                <td className="px-3 py-2 text-right">
                  {editing === r.sector_id ? (
                    <span className="inline-flex gap-2">
                      <button onClick={() => rename(r.sector_id)} data-testid={`sector-save-${r.sector_id}`}><Check className="h-4 w-4 text-green-600" /></button>
                      <button onClick={() => setEditing(null)} data-testid={`sector-cancel-${r.sector_id}`}><X className="h-4 w-4 text-slate-400" /></button>
                    </span>
                  ) : (
                    <button onClick={() => { setEditing(r.sector_id); setEditName(r.name); }} data-testid={`sector-rename-${r.sector_id}`}>
                      <Pencil className="h-3.5 w-3.5 text-slate-400" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ClientSectorMaster;
